// GraphRuntime: contract for using a procedural graph as a scene instance.
// The implementation lives in the engine (fluveria-graph via
// GraphRuntimeHandle): graph parsing, plans, and per-sample geometry/material
// evaluation run in Rust. TS keeps only the shape-compile cache plumbing and
// pushes compiled shapes into the handle whenever the compile key changes.

import { GraphRuntimeHandle, type ScenePainter } from 'fluveria-engine-wasm';
import type { Vec2 } from '../scene/layoutMath.js';
import type { GraphNode, GraphEdge, OutputValue, ParamStore, Size } from './nodeDefs.js';
import { isValueNode } from './nodeDefs.js';
import { scopedParamKey, type ParamGraphSlot } from './paramKeys.js';
import { buildGraphCompilePlan, type DrawBounds, type GeometryEvalResult, type GraphCompilePlan } from './engine.js';
import type { ArcCoverageMode } from '../sdf/arcCoverageMode.js';
import type { DocumentLight } from './lights.js';
import { compileShapeGraphToShape, type ShapeCompileResult } from '../sdf/shapeCompiler.js';

export type { Size };

export interface Space {
  min: Size;
  max: Size;
}

export type Vec4 = [number, number, number, number];

/**
 * Runtime contract for a graph used inside a scene instance.
 *
 * - `measure` must be pure.
 * - `sdf` / `render` are optional — a graph may declare only measure
 *   if it's purely structural.
 */
export interface GraphRuntime {
  measure(params: Record<string, unknown>, space: Space): Size;
  geometry(layerPoint: Vec2, params: Record<string, unknown>, layerSize: Size, sampleFrame?: RuntimeSampleFrame): GeometryEvalResult | null;
  material?(layerPoint: Vec2, params: Record<string, unknown>, layerSize: Size, geometry: GeometryEvalResult, sampleFrame?: RuntimeSampleFrame): OutputValue | null;
  /**
   * `worldScale` is the layer's framebuffer pixels per shape unit, the same
   * pair `syncShape` gets. The engine reads the expansion in pixels and the
   * bounds in shape units, so without it a dilation is off by the zoom.
   * Omitted, the engine falls back to the scale-1 reading it took before
   * the arguments existed.
   */
  materialDrawBounds(params: Record<string, unknown>, allocatedSize?: Size, worldScale?: Vec2): DrawBounds;
  dynamicDrawBoundsDilationPx?(renderSlot: 'geometry' | 'material'): number;
  /**
   * Batched CPU render: paint this layer's pixel rect into the engine-side
   * framebuffer in a single boundary call (one per layer instead of two per
   * pixel). The painter and args carry everything the per-pixel loop needs.
   */
  paintTile?(painter: ScenePainter, params: Record<string, unknown>, layerSize: Size, args: LayerPaintArgs): void;
  readonly sizeMode: IntrinsicSizeMode;
  /**
   * Identity refs of the graphs this runtime was built from. Runtimes are
   * recreated per frame, so dirty-rect diffing compares these refs (the
   * editor updates graph arrays immutably) instead of runtime identity.
   */
  readonly contentKey?: GraphRuntimeContentKey;
  /**
   * Param-store keys this runtime's graphs can read (scoped + legacy keys of
   * exposed value nodes). Lets dirty-rect diffing react only to params that
   * actually feed this layer instead of any params-object replacement.
   */
  readonly paramKeys?: ReadonlySet<string>;
}

export interface GraphRuntimeContentKey {
  geometryNodes: GraphNode[];
  geometryEdges: GraphEdge[];
  material: RuntimeMaterialGraph | null;
}

/** Per-layer arguments for ScenePainter.paintLayer (engine scene_painter.rs). */
export interface LayerPaintArgs {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** 2D affine local-from-world [a, b, c, d, tx, ty]. */
  toLocal: [number, number, number, number, number, number];
  allocatedW: number;
  allocatedH: number;
  offsetX: number;
  offsetY: number;
  wsX: number;
  wsY: number;
  virtualW: number;
  virtualH: number;
  density: number;
  layerFw: number;
  /**
   * `[minX, minY, maxX, maxY]` in layer units, the min sides negative for
   * expansion. Four sides, not two: a bleed declared on one side must not put
   * a skirt on the opposite one. Produced by the engine's own `planLayerPaint`
   * and handed straight back to it, so this type only has to describe what
   * comes across truthfully.
   */
  drawBoundsLocal: [number, number, number, number];
  /**
   * `[x0, y0, x1, y1]` in layer units: the clip a clipping frame above makes
   * where it is turned and the rectangle `x0..y1` cannot hold it, cut at for
   * each pixel. Turned inside out (`NO_LOCAL_CLIP`) is none. Not part of the
   * plan: the scene walk adds it.
   */
  clipLocal: [number, number, number, number];
}

export interface RuntimeSampleFrame {
  point: Vec2;
  size: Size;
  pixelsPerShapeUnitX?: number;
  pixelsPerShapeUnitY?: number;
}

/** How one axis of a layer's intrinsic size is determined. */
export type AxisSizing = 'fill' | 'content';

/**
 * The canonical size mode is per-axis (`axes`); the legacy whole-layer kinds
 * `fill`/`layoutBounds` are accepted on input only — the engine converts them
 * on document load (fill -> axes(fill,fill), layoutBounds ->
 * axes(content,content)) and always serializes `axes`.
 */
export type IntrinsicSizeMode =
  | { kind: 'axes'; width: AxisSizing; height: AxisSizing }
  | { kind: 'fill' }
  | { kind: 'layoutBounds' }
  | { kind: 'fixed'; width: number; height: number }
  | { kind: 'param-driven'; widthParam: string; heightParam: string; fallbackWidth: number; fallbackHeight: number };

/** Which LayerSize outputs a compile may read (mirrors the engine's gate). */
export type LayerSizeAccess = 'full' | 'deny' | 'width-only' | 'height-only';

/** Per-axis content flags, with the legacy kinds converted. */
export function contentAxes(mode: IntrinsicSizeMode): { width: boolean; height: boolean } {
  switch (mode.kind) {
    case 'axes': return { width: mode.width === 'content', height: mode.height === 'content' };
    case 'layoutBounds': return { width: true, height: true };
    default: return { width: false, height: false };
  }
}

/** Whether any axis hugs the shape content (needs a measure pre-compile). */
export function hasContentAxis(mode: IntrinsicSizeMode): boolean {
  const axes = contentAxes(mode);
  return axes.width || axes.height;
}

export type GraphRuntimeResolver = (graphId: string) => GraphRuntime | null;

export type RuntimeColormap = 'gray' | 'inferno' | 'plasma' | 'turbo';

export interface GraphRuntimeRenderSettings {
  colormap: RuntimeColormap;
  aaMode: 'none' | 'fwidth' | 'msaa2x';
  aaWidth: number;
  arcCoverageMode: ArcCoverageMode;
  /** Active viewport render theme — mode 0/1/2 and resolved theme 0/1 (light/dark). */
  renderTheme?: { mode: number; resolvedTheme: number };
}

export interface RuntimeMaterialGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface GraphRuntimeOptions {
  graphId: string;
  renderSettings: GraphRuntimeRenderSettings;
  sizeMode?: IntrinsicSizeMode;
  shapes?: Record<string, unknown>;
  /**
   * The document's lamps, which the material's Illuminance nodes sum: the
   * canvas path samples the material here, and lights it as the WebGPU path
   * lights it. None is a document without lamps.
   */
  lights?: DocumentLight[];
}

/**
 * Minimum runtime pixel density used when converting between screen pixels,
 * material sample units, and shape units. Must match
 * MIN_RUNTIME_PIXEL_DENSITY in the engine's runtime.rs.
 */
const MIN_RUNTIME_PIXEL_DENSITY = 1e-6;

/**
 * GraphRuntimeHandle owns wasm-side state (plans, compiled shape sampler);
 * runtimes are cached per layer and dropped wholesale on scene reload, so
 * free the handle when its JS runtime object is collected — same pattern as
 * shapeEngine's arc sampler finalizer.
 */
/**
 * Param-store keys readable by the given graphs: scoped + legacy keys of
 * exposed value nodes (mirrors the engine's `read_param_value` lookup).
 */
function collectParamKeys(
  graphId: string,
  geometryNodes: GraphNode[],
  material: RuntimeMaterialGraph | null,
): ReadonlySet<string> {
  const keys = new Set<string>();
  const collect = (nodes: GraphNode[], slot: ParamGraphSlot): void => {
    for (const node of nodes) {
      if (!isValueNode(node)) continue;
      if (!(node.data as { exposed?: boolean }).exposed) continue;
      keys.add(scopedParamKey(graphId, node.id, slot));
      const legacy = `param:layer:${graphId}:${slot}:${node.id}`;
      if (!graphId.includes(':')) keys.add(legacy);
    }
  };
  collect(geometryNodes, 'geometry');
  if (material) collect(material.nodes, 'material');
  return keys;
}

const runtimeHandleFinalizer = typeof FinalizationRegistry === 'undefined'
  ? null
  : new FinalizationRegistry<GraphRuntimeHandle>(handle => {
      handle.free();
    });

/**
 * Create a GraphRuntime for the given nodes/edges, backed by the engine's
 * GraphRuntimeHandle.
 *
 * `sizeMode` declares how the graph reports its intrinsic size.
 * Default is `fill` — the graph expands to the allocated layer bounds when
 * arranged/rendered, but contributes no intrinsic size during layout measure.
 */
export function createGraphRuntime(
  geometryNodes: GraphNode[],
  geometryEdges: GraphEdge[],
  material: RuntimeMaterialGraph | null,
  options: GraphRuntimeOptions,
): GraphRuntime {
  const { graphId, renderSettings, sizeMode = { kind: 'fill' }, shapes, lights } = options;
  const handle = new GraphRuntimeHandle({
    graphId,
    geometry: { nodes: geometryNodes, edges: geometryEdges },
    material,
    renderSettings,
    sizeMode,
  });
  if (lights?.length) handle.setLights(lights);
  let cachedGeometryPlan: GraphCompilePlan | null = null;
  let cachedShapeQuery: {
    params: Record<string, unknown>;
    width: number;
    height: number;
    pixelsPerShapeUnit: number;
    pixelsPerShapeUnitX: number;
    pixelsPerShapeUnitY: number;
    layerSizeAccess: LayerSizeAccess;
    shape: ShapeCompileResult;
  } | null = null;
  let syncedParams: Record<string, unknown> | null = null;
  let syncedShapeKey: string | null = null;

  function getGeometryPlan(): GraphCompilePlan {
    if (!cachedGeometryPlan) cachedGeometryPlan = buildGraphCompilePlan(geometryNodes, geometryEdges);
    return cachedGeometryPlan;
  }

  function pixelsPerShapeUnit(sampleFrame?: RuntimeSampleFrame): { x: number; y: number; value: number } {
    const x = Math.max(sampleFrame?.pixelsPerShapeUnitX ?? 1, MIN_RUNTIME_PIXEL_DENSITY);
    const y = Math.max(sampleFrame?.pixelsPerShapeUnitY ?? 1, MIN_RUNTIME_PIXEL_DENSITY);
    return { x, y, value: Math.max(Math.min(x, y), MIN_RUNTIME_PIXEL_DENSITY) };
  }

  function toGraphParams(params: Record<string, unknown>): ParamStore {
    const graphParams: ParamStore = {};
    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'number' || typeof value === 'string') {
        graphParams[key] = value;
      } else if (Array.isArray(value)) {
        graphParams[key] = value.map(entry => Number(entry));
      }
    }
    return graphParams;
  }

  function syncParams(params: Record<string, unknown>): void {
    if (syncedParams === params) return;
    handle.setParams(toGraphParams(params));
    syncedParams = params;
  }

  function syncShape(shape: ShapeCompileResult): void {
    if (syncedShapeKey === shape.key) return;
    syncedShapeKey = shape.key;
    handle.setCompiledShapeFromArcs(
      shape.query ? shape.query.arcs : new Float32Array(),
      shape.query ? shape.query.contourOffsets : new Uint32Array(1),
      !!shape.query,
      new Float64Array([shape.layoutBounds.minX, shape.layoutBounds.minY, shape.layoutBounds.maxX, shape.layoutBounds.maxY]),
      new Float64Array([shape.contourBounds.minX, shape.contourBounds.minY, shape.contourBounds.maxX, shape.contourBounds.maxY]),
    );
  }

  function getCompiledShape(params: Record<string, unknown>, layerSize: Size, sampleFrame?: RuntimeSampleFrame, layerSizeAccess: LayerSizeAccess = 'full'): ShapeCompileResult {
    const shapeDensity = pixelsPerShapeUnit(sampleFrame);
    if (
      cachedShapeQuery
      && cachedShapeQuery.params === params
      && cachedShapeQuery.width === layerSize.w
      && cachedShapeQuery.height === layerSize.h
      && cachedShapeQuery.pixelsPerShapeUnit === shapeDensity.value
      && cachedShapeQuery.pixelsPerShapeUnitX === shapeDensity.x
      && cachedShapeQuery.pixelsPerShapeUnitY === shapeDensity.y
      && cachedShapeQuery.layerSizeAccess === layerSizeAccess
    ) {
      return cachedShapeQuery.shape;
    }
    const result = compileShapeGraphToShape(getGeometryPlan(), {
      graphId,
      params: toGraphParams(params),
      layerSize: [layerSize.w, layerSize.h],
      shapeUnit: 'px',
      pixelsPerShapeUnit: shapeDensity.value,
      pixelsPerShapeUnitX: shapeDensity.x,
      pixelsPerShapeUnitY: shapeDensity.y,
      layerSizeAccess,
      shapes,
    });
    cachedShapeQuery = {
      params,
      width: layerSize.w,
      height: layerSize.h,
      pixelsPerShapeUnit: shapeDensity.value,
      pixelsPerShapeUnitX: shapeDensity.x,
      pixelsPerShapeUnitY: shapeDensity.y,
      layerSizeAccess,
      shape: result,
    };
    return result;
  }

  function syncForSampling(params: Record<string, unknown>, layerSize: Size, sampleFrame?: RuntimeSampleFrame): void {
    syncParams(params);
    syncShape(getCompiledShape(params, layerSize, sampleFrame));
  }

  function measure(params: Record<string, unknown>, space: Space): Size {
    syncParams(params);
    const axes = contentAxes(sizeMode);
    if (axes.width || axes.height) {
      // A fill axis keeps its LayerSize port readable during measure (the box
      // stands in as space.max — the engine-side degrade rule); reading a
      // content axis back would be circular and stays denied.
      const access: LayerSizeAccess = axes.width && axes.height
        ? 'deny'
        : axes.height ? 'width-only' : 'height-only';
      const shape = getCompiledShape(params, space.max, undefined, access);
      if (shape.error || !shape.layoutBounds) return { w: 0, h: 0 };
      syncShape(shape);
    }
    const [w, h] = handle.measure();
    return { w, h };
  }

  function geometry(layerPoint: Vec2, params: Record<string, unknown>, layerSize: Size, sampleFrame?: RuntimeSampleFrame): GeometryEvalResult | null {
    syncForSampling(params, layerSize, sampleFrame);
    return handle.geometry(layerPoint[0], layerPoint[1], layerSize.w, layerSize.h, sampleFrame ?? null) as GeometryEvalResult;
  }

  function shadeMaterial(
    layerPoint: Vec2,
    params: Record<string, unknown>,
    layerSize: Size,
    _geometryResult: GeometryEvalResult,
    sampleFrame?: RuntimeSampleFrame,
  ): OutputValue | null {
    if (!material) return null;
    syncForSampling(params, layerSize, sampleFrame);
    return handle.material(layerPoint[0], layerPoint[1], layerSize.w, layerSize.h, sampleFrame ?? null) as OutputValue | null;
  }

  function getMaterialDrawBounds(params: Record<string, unknown>, allocatedSize?: Size, worldScale?: Vec2): DrawBounds {
    if (!material) return [0, 0, 0, 0];
    syncParams(params);
    const layerSize = allocatedSize ?? { w: 1, h: 1 };
    // 0 is the engine's documented "no scale given" reading, not a real scale.
    return Array.from(handle.materialDrawBounds(
      layerSize.w,
      layerSize.h,
      worldScale?.[0] ?? 0,
      worldScale?.[1] ?? 0,
    )) as DrawBounds;
  }

  function dynamicDrawBoundsDilationPx(renderSlot: 'geometry' | 'material'): number {
    return handle.dynamicDrawBoundsDilationPx(renderSlot);
  }

  function paintTile(painter: ScenePainter, params: Record<string, unknown>, layerSize: Size, args: LayerPaintArgs): void {
    syncForSampling(params, layerSize, {
      point: [0, 0],
      size: layerSize,
      pixelsPerShapeUnitX: args.wsX,
      pixelsPerShapeUnitY: args.wsY,
    });
    painter.paintLayer(handle, args);
  }

  const contentKey: GraphRuntimeContentKey = { geometryNodes, geometryEdges, material };
  const paramKeys = collectParamKeys(graphId, geometryNodes, material);

  const runtime: GraphRuntime = { measure, geometry, material: shadeMaterial, materialDrawBounds: getMaterialDrawBounds, dynamicDrawBoundsDilationPx, paintTile, sizeMode, contentKey, paramKeys };
  runtimeHandleFinalizer?.register(runtime, handle);
  return runtime;
}

/** Invalidate the cached eval plan (call when graph topology changes). */
export function invalidateRuntime(runtime: GraphRuntime): void {
  // The runtime closure holds its own cachedPlan; to invalidate,
  // create a new runtime. This function exists as a contract placeholder
  // for future dirty-flag integration.
  void runtime;
}
